import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import ComingSoon from './components/ComingSoon';
import Landing from './pages/Landing';
import About from './pages/About';
import Projects from './pages/Projects';
import Articles from './pages/Articles';
import Contact from './pages/Contact';
import Documentation from './pages/Documentation';
import './styles/globals.css';

// HTML5 API Integration Hook
const useHTML5Features = () => {
  useEffect(() => {
    // Initialize Web Storage for user preferences
    const initializeStorage = () => {
      if ('localStorage' in window) {
        const theme = localStorage.getItem('griffin-theme') || 'dark';
        document.documentElement.setAttribute('data-theme', theme);
        
        // Store app version for cache management
        localStorage.setItem('griffin-app-version', '1.2.0');
        
        // Initialize user analytics with consent
        const analyticsConsent = localStorage.getItem('griffin-analytics-consent');
        if (analyticsConsent === null) {
          // Could show consent banner here
          console.log('Analytics consent not set');
        }
      }
    };

    // Initialize Geolocation (with user permission)
    const initializeGeolocation = () => {
      if ('geolocation' in navigator) {
        // Store geolocation capability
        sessionStorage.setItem('geolocation-available', 'true');
      }
    };

    // Initialize Performance Observer for Core Web Vitals
    const initializePerformanceObserver = () => {
      if ('PerformanceObserver' in window) {
        try {
          // Monitor First Input Delay
          const observer = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
              if (entry.entryType === 'first-input') {
                // Type assertion for PerformanceEventTiming
                const fidEntry = entry as PerformanceEventTiming;
                if (fidEntry.processingStart) {
                  console.log('FID:', fidEntry.processingStart - fidEntry.startTime);
                }
              }
            }
          });
          observer.observe({ entryTypes: ['first-input'] });
        } catch (e) {
          console.log('Performance monitoring not supported');
        }
      }
    };

    // Initialize Intersection Observer for lazy loading
    const initializeIntersectionObserver = () => {
      if ('IntersectionObserver' in window) {
        // Enhanced lazy loading for images and components
        const imageObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const img = entry.target as HTMLImageElement;
              if (img.dataset.src) {
                img.src = img.dataset.src;
                img.removeAttribute('data-src');
                imageObserver.unobserve(img);
              }
            }
          });
        });

        // Store observer in global scope for component use
        (window as any).griffinImageObserver = imageObserver;
      }
    };

    // Initialize Web Workers for background tasks
    const initializeWebWorkers = () => {
      if ('Worker' in window) {
        try {
          // Could initialize background workers for data processing
          sessionStorage.setItem('web-workers-available', 'true');
        } catch (e) {
          console.log('Web Workers not available');
        }
      }
    };

    // Initialize Notification API
    const initializeNotifications = () => {
      if ('Notification' in window) {
        if (Notification.permission === 'default') {
          // Store notification capability but don't request permission yet
          sessionStorage.setItem('notifications-available', 'true');
        }
      }
    };

    // Initialize all HTML5 features
    initializeStorage();
    initializeGeolocation();
    initializePerformanceObserver();
    initializeIntersectionObserver();
    initializeWebWorkers();
    initializeNotifications();

    // Clean up function
    return () => {
      if ((window as any).griffinImageObserver) {
        (window as any).griffinImageObserver.disconnect();
      }
    };
  }, []);
};

function AppContent() {
  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  
  // Initialize HTML5 features
  useHTML5Features();

  // Service Worker message handler
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleSWMessage = (event: MessageEvent) => {
        if (event.data && event.data.type === 'BACK_ONLINE') {
          console.log('App back online:', event.data.timestamp);
          // Could show a toast notification here
        }
      };

      navigator.serviceWorker.addEventListener('message', handleSWMessage);
      
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      };
    }
  }, []);

  // Enhanced page tracking with Web Storage
  useEffect(() => {
    // Scroll to top on route change
    window.scrollTo(0, 0);
    
    if ('localStorage' in window) {
      const pageViews = JSON.parse(localStorage.getItem('griffin-page-views') || '{}');
      pageViews[location.pathname] = (pageViews[location.pathname] || 0) + 1;
      pageViews.lastVisited = new Date().toISOString();
      localStorage.setItem('griffin-page-views', JSON.stringify(pageViews));
    }

    // Update document title with semantic information
    const pageTitle = getPageTitle(location.pathname);
    document.title = pageTitle;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (metaDescription) {
      metaDescription.content = getPageDescription(location.pathname);
    }
  }, [location.pathname]);

  return (
    <div 
      className={`App ${isLandingPage ? 'App--landing' : ''}`}
      role="application"
      aria-label="Griffin's World Application"
    >
      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="visually-hidden skip-link">
        Skip to main content
      </a>
      
      {/* Header with semantic HTML5 */}
      {!isLandingPage && (
        <Header />
      )}
      
      {/* Main content area with semantic HTML5 */}
      <main 
        id="main-content" 
        className="main-content"
        role="main"
        aria-label="Main application content"
      >
        <Routes>
          <Route path="/" element={<Landing key="landing" />} />
          <Route path="/about" element={<About key="about" />} />
          <Route path="/store" element={<ComingSoon key="store" />} />
          <Route path="/projects" element={<Projects key="projects" />} />
          <Route path="/articles" element={<Articles key="articles" />} />
          <Route path="/docs" element={<Documentation key="docs" />} />
          <Route path="/contact" element={<Contact key="contact" />} />
        </Routes>
      </main>
      
      {/* Footer with semantic HTML5 */}
      {!isLandingPage && (
        <Footer />
      )}
      
      {/* Connection status indicator */}
      <ConnectionStatus />
    </div>
  );
}

// Connection Status Component using HTML5 APIs
const ConnectionStatus: React.FC = () => {
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      console.log('App is back online');
    };

    const handleOffline = () => {
      setIsOnline(false);
      console.log('App is offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (isOnline) return null;

  return (
    <aside 
      className="connection-status connection-status--offline"
      role="alert"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: '1rem',
        right: '1rem',
        background: '#ff0040',
        color: '#fff',
        padding: '0.5rem 1rem',
        borderRadius: '0.25rem',
        fontFamily: 'var(--font-terminal)',
        fontSize: '0.875rem',
        zIndex: 9999,
        border: '1px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)'
      }}
    >
      📡 Offline Mode
    </aside>
  );
};

// Utility functions for SEO and accessibility
function getPageTitle(pathname: string): string {
  const titles: Record<string, string> = {
    '/': 'Griffin\'s World - Digital Innovation Hub',
    '/about': 'About - Griffin\'s World',
    '/projects': 'Projects - Griffin\'s World',
    '/store': 'Store - Griffin\'s World',
    '/articles': 'Articles - Griffin\'s World',
    '/docs': 'Documentation - Griffin\'s World',
    '/contact': 'Contact - Griffin\'s World'
  };
  
  return titles[pathname] || 'Griffin\'s World';
}

function getPageDescription(pathname: string): string {
  const descriptions: Record<string, string> = {
    '/': 'Explore cutting-edge web development, digital innovation, and creative technology solutions.',
    '/about': 'Learn about Griffin\'s background, expertise, and passion for innovative web development.',
    '/projects': 'Discover innovative projects showcasing modern web development and creative technology.',
    '/store': 'Coming soon - Premium digital tools and applications from Griffin\'s development arsenal.',
    '/articles': 'Read insights, tutorials, and thoughts on web development and technology trends.',
    '/docs': 'Comprehensive documentation, guides, and references for all Griffin\'s tools and projects.',
    '/contact': 'Get in touch for collaborations, projects, or to discuss innovative web solutions.'
  };
  
  return descriptions[pathname] || 'Griffin\'s World - Digital Innovation Hub';
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
